import { StateCreator } from "zustand";

import {
  ErrorPayloadType,
  PlayerEvent,
  PlayerEventCallback
} from "../../types/common";
import { PlayerStoreProperties } from "../store";
import { StoreMiddlewares } from "../storeTypes";
import { isCallable } from "../storeUtils";

export type Player = {
  autoplay?: () => void;
  enterFullscreen?: () => void;
  exitFullscreen?: () => void;
  pause: () => void;
  play: (options?: { throwError?: boolean }) => Promise<void>;
  requestPictureInPicture?: () => void;
  seekFor: (amount: number) => void;
  seekTo: (newCurrentTime: number) => void;
  setMuted: (isMuted: boolean, persist?: boolean) => void;
  setPlaybackRate: (playbackRate: number) => void;
  setVolume: (volume: number, persist?: boolean) => void;
  type: "audio" | "video" | "chromecast" | undefined;
};

export type ExternalEvents = {
  addListener: (event: PlayerEvent, cb: PlayerEventCallback) => void;
  dispatch: (event: PlayerEvent) => void;
  removeListener: (event: PlayerEvent, cb: PlayerEventCallback) => void;
  removeListeners: () => void;
};

type HlsJs = {
  startLoad: () => void;
  stopLoad: () => void;
};

export type MediaElementSliceState = {
  error: ErrorPayloadType | undefined;
  externalEvents: ExternalEvents | undefined;
  hlsJs: HlsJs;
  isAdaptiveBitrate: boolean;
  isAutoplay: boolean;
  isDestroyed: boolean;
  isEnded: boolean;
  isLoading: boolean;
  isMuted: boolean;
  isPaused: boolean;
  isPlaybackStarted: boolean;
  isPlayerSetupDone: boolean;
  isPlayerSetupStarted: boolean;
  playbackRate: number;
  player: Player;
  volume: number;
};

type MediaElementSliceActions = {
  setError: (error?: ErrorPayloadType) => void;
  setExternalEvents: (externalEvents: ExternalEvents) => void;
  setHlsJs: (hlsJs: HlsJs) => void;
  setIsAdaptiveBitrate: (isAdaptiveBitrate: boolean) => void;
  setIsAutoplay: (isAutoplay: boolean) => void;
  setIsDestroyed: (isDestroyed: boolean) => void;
  setIsEnded: (isEnded: boolean) => void;
  setIsLoading: (isLoading: boolean) => void;
  setIsMuted: (isMuted: boolean) => void;
  setIsPaused: (isPaused: boolean) => void;
  setIsPlaybackStarted: (isPlaybackStarted: boolean) => void;
  setIsPlayerSetupDone: (isPlayerSetupDone: boolean) => void;
  setIsPlayerSetupStarted: (isPlayerSetupStarted: boolean) => void;
  setPlaybackRate: (playbackRate: number) => void;
  setPlayer: (player: Player | undefined) => void;
  setVolume: (volume: number) => void;
};

export type MediaElementSlice = MediaElementSliceState &
  MediaElementSliceActions;

const playerNotSetFn = (): void => {
  console.warn("Player is not set");
};

const defaultPlayer: Player = {
  enterFullscreen: playerNotSetFn,
  exitFullscreen: playerNotSetFn,
  pause: playerNotSetFn,
  play: () => new Promise(playerNotSetFn),
  seekFor: playerNotSetFn,
  seekTo: playerNotSetFn,
  setMuted: playerNotSetFn,
  setPlaybackRate: playerNotSetFn,
  setVolume: playerNotSetFn,
  type: undefined
};

export const MEDIA_ELEMENT_SLICE_DEFAULTS: MediaElementSliceState = {
  error: undefined,
  externalEvents: undefined,
  hlsJs: {
    startLoad: () => console.warn("startLoad is not set"),
    stopLoad: () => console.warn("stopLoad is not set")
  },
  isAdaptiveBitrate: true,
  isAutoplay: false,
  isDestroyed: false,
  isEnded: false,
  isLoading: false,
  isMuted: false,
  isPaused: true,
  isPlaybackStarted: false,
  isPlayerSetupDone: false,
  isPlayerSetupStarted: false,
  playbackRate: 1,
  player: defaultPlayer,
  volume: 1
};

export const mediaElementSlice: StateCreator<
  PlayerStoreProperties,
  StoreMiddlewares,
  [],
  MediaElementSlice
> = set => ({
  ...MEDIA_ELEMENT_SLICE_DEFAULTS,
  setError: error => set({ error }, false, "setError"),
  setExternalEvents: externalEvents =>
    set({ externalEvents }, false, "setExternalEvents"),
  setHlsJs: hlsJs =>
    set(
      state => {
        state.hlsJs = hlsJs;
      },
      false,
      "setHlsJs"
    ),
  setIsAdaptiveBitrate: isAdaptiveBitrate =>
    set({ isAdaptiveBitrate }, false, "setIsAdaptiveBitrate"),
  setIsAutoplay: isAutoplay => set({ isAutoplay }, false, "setIsAutoplay"),
  setIsDestroyed: isDestroyed => set({ isDestroyed }, false, "setIsDestroyed"),
  setIsEnded: isEnded => set({ isEnded }, false, "setIsEnded"),
  setIsLoading: isLoading => set({ isLoading }, false, "setIsLoading"),
  setIsMuted: isMuted => set({ isMuted }, false, "setIsMuted"),
  setIsPaused: isPaused =>
    set(
      state => {
        state.isPaused = isPaused;
        if (!isPaused) {
          state.isPlaybackStarted = true;
        }
      },
      false,
      "setIsPaused"
    ),
  setIsPlaybackStarted: isPlaybackStarted =>
    set({ isPlaybackStarted }, false, "setIsPlaybackStarted"),
  setIsPlayerSetupDone: isPlayerSetupDone =>
    set({ isPlayerSetupDone }, false, "setIsPlayerSetupDone"),
  setIsPlayerSetupStarted: isPlayerSetupStarted =>
    set({ isPlayerSetupStarted }, false, "setIsPlayerSetupStarted"),
  setPlaybackRate: playbackRate =>
    set(
      state => {
        const rate = isCallable(playbackRate)
          ? playbackRate(state.playbackRate)
          : playbackRate;
        state.playbackRate = Math.max(rate, 0);
      },
      false,
      "setPlaybackRate"
    ),
  setPlayer: playerController =>
    set(
      state => {
        state.player = playerController || defaultPlayer;
      },
      false,
      "setPlayer"
    ),
  setVolume: volume => set({ volume }, false, "setVolume")
});
